import React, { useContext, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CartContext } from "../Component/CartContext";
import productData from "../Datas/productData";
import Header from "./Header";
import Footer from "./Footer";
import "../css/cartpage.scss";

const CartPage = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity } = useContext(CartContext);

  const cartLines = useMemo(
    () =>
      cartItems
        .map((item) => {
          const product = productData.find((entry) => entry.id === item.id);
          if (!product) {
            return null;
          }
          const unitPrice = Number(product.price);
          return {
            key: `${product.id}-${item.size || "default"}`,
            product,
            cart: item,
            unitPrice: Number.isNaN(unitPrice) ? 0 : unitPrice,
          };
        })
        .filter(Boolean),
    [cartItems]
  );

  const subtotal = cartLines.reduce(
    (sum, line) => sum + line.unitPrice * line.cart.quantity,
    0
  );

  const formatPrice = (value) => {
    const numeric = Number(value);
    if (Number.isNaN(numeric)) {
      return value;
    }
    return `€ ${numeric.toLocaleString("en-EU", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const handleQuantityChange = (id, size, delta, currentQuantity) => {
    const nextQuantity = currentQuantity + delta;
    if (nextQuantity < 1) return;
    updateQuantity(id, size, nextQuantity);
  };

  const handleCheckout = () => {
    if (cartLines.length > 0) {
      navigate("/checkout");
    } else {
      alert("Your cart is empty.");
    }
  };

  const handleRemove = (id, size) => {
    removeFromCart(id, size);
  };

  const isVideo = (url) => /\.(mp4|webm|ogg)(\?.*)?$/i.test(url);

  const deliveryFee = 0;
  const total = subtotal + deliveryFee;
  const toTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  if (cartLines.length === 0) {
    return (
      <div className="cart-page color-scheme color-scheme--scheme-1">
        <Header />
        <main className="cart-page__main cart-page__main--empty">
          <section className="cart-empty">
            <h1 className="cart-empty__title">Your shopping bag is empty</h1>
            <p className="cart-empty__subtitle">
              Discover our latest arrivals and add your favorites to the bag.
            </p>
            <Link className="cart-empty__cta" to="/shop" onClick={toTop}>
              Continue shopping
            </Link>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="cart-page color-scheme color-scheme--scheme-1">
      <Header />
      <main className="cart-page__main">
        <div className="cart-page__layout">
          <section className="cart-page__bag" aria-label="Shopping bag items">
            <header className="cart-page__bag-header">
              <h1 className="cart-page__title">
                Shopping Bag ({cartLines.length})
              </h1>
            </header>
            <div className="cart-page__items">
              {cartLines.map(({ key, product, cart, unitPrice }) => {
                const primaryImage = product.images?.[0];
                const displayPrice = formatPrice(unitPrice);

                return (
                  <article className="cart-item" key={key}>
                    <div className="cart-item__media">
                      {primaryImage ? (
                        isVideo(primaryImage) ? (
                          <video src={primaryImage} autoPlay muted loop playsInline />
                        ) : (
                          <img src={primaryImage} alt={product.name} />
                        )
                      ) : (
                        <div className="cart-item__media-placeholder">
                          No image
                        </div>
                      )}
                      <button
                        type="button"
                        className="cart-item__remove"
                        aria-label="Remove item from cart"
                        onClick={() => handleRemove(product.id, cart.size)}
                      >
                        &times;
                      </button>
                    </div>

                    <div className="cart-item__details">
                      <div className="cart-item__info">
                        <div>
                          <p className="cart-item__name">{product.name}</p>
                          <p className="cart-item__price">{displayPrice}</p>
                        </div>
                      </div>

                      <div className="cart-item__meta">
                        <div className="cart-item__meta-group">
                          <span className="cart-item__meta-label">
                            Quantity
                          </span>
                          <div className="cart-item__quantity-control">
                            <button
                              type="button"
                              onClick={() =>
                                handleQuantityChange(
                                  product.id,
                                  cart.size,
                                  -1,
                                  cart.quantity
                                )
                              }
                              aria-label="Decrease quantity"
                            >
                              -
                            </button>
                            <span>{cart.quantity}</span>
                            <button
                              type="button"
                              onClick={() =>
                                handleQuantityChange(
                                  product.id,
                                  cart.size,
                                  1,
                                  cart.quantity
                                )
                              }
                              aria-label="Increase quantity"
                            >
                              +
                            </button>
                          </div>
                        </div>

                        {cart.size && (
                          <div className="cart-item__meta-group">
                            <span className="cart-item__meta-label">Size</span>
                            <span className="cart-item__meta-value">
                              {cart.size}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>

          <aside className="cart-page__summary" aria-label="Order summary">
            <div className="cart-summary">
              <h2 className="cart-summary__title">Summary</h2>
              <dl className="cart-summary__totals">
                <div className="cart-summary__row">
                  <dt>Subtotal</dt>
                  <dd>{formatPrice(subtotal)}</dd>
                </div>
                <div className="cart-summary__row">
                  <dt>Delivery</dt>
                  <dd>
                    {deliveryFee === 0 ? "Free" : formatPrice(deliveryFee)}
                  </dd>
                </div>
              </dl>
              <div className="cart-summary__total">
                <span>Total</span>
                <span>{formatPrice(total)}</span>
              </div>
              <p className="cart-summary__tax-note">Taxes included</p>
              <button
                type="button"
                className="cart-summary__checkout"
                onClick={handleCheckout}
              >
                Checkout
              </button>
            </div>
          </aside>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CartPage;
